import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Link,
  Menu,
  MenuItem,
  Skeleton,
  Stack,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import {
  DataGridPro,
  GridColDef,
  GridRowSelectionModel,
} from "@mui/x-data-grid-pro";
import { useSnackbar } from "notistack";

import { CreateFab2 } from "../components/Buttons";
import AlertDialog from "../components/AlertDialog";
import { actionsMenuButtonSx } from "../components/dropdownMenuStyles";
import { commonDataGridPremiumSx } from "../utils/dataGridSx";
import {
  createUrl,
  deleteUrl,
  getUrls,
} from "./api";
import UrlDrawer from "./UrlDrawer";
import type { Url } from "./types";

interface Props {
  source: string;
  sourceId: number;
  canEdit?: boolean;
}

export default function UrlsTab(props: Props) {
  const { source, sourceId, canEdit = true } = props;
  const { enqueueSnackbar } = useSnackbar();

  const [links, setLinks] = useState<Url[]>([]);
  const [loading, setLoading] = useState(true);
  const [reloadKey, setReloadKey] = useState(0);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingUrl, setEditingUrl] = useState<Url | null>(null);
  const [pendingDelete, setPendingDelete] = useState<Url[] | null>(null);
  const [rowSelectionModel, setRowSelectionModel] =
    useState<GridRowSelectionModel>([]);
  const [actionsAnchorEl, setActionsAnchorEl] = useState<HTMLElement | null>(
    null
  );

  const fetchLinks = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await getUrls(source, sourceId);
      setLinks(resp.data);
    } catch {
      enqueueSnackbar("Error loading links", { variant: "error" });
    } finally {
      setLoading(false);
    }
  }, [source, sourceId, enqueueSnackbar]);

  useEffect(() => {
    fetchLinks();
  }, [fetchLinks, reloadKey]);

  const openAdd = () => {
    setEditingUrl(null);
    setDrawerOpen(true);
  };

  const openEdit = (row: Url) => {
    setEditingUrl(row);
    setDrawerOpen(true);
  };

  const handleSaved = () => {
    setReloadKey((k) => k + 1);
  };

  const confirmDelete = (rows: Url[]) => {
    setPendingDelete(rows);
  };

  const handleConfirmedDelete = async () => {
    if (!pendingDelete) return;
    try {
      for (const row of pendingDelete) {
        await deleteUrl(source, sourceId, row.id);
      }
      enqueueSnackbar(
        pendingDelete.length === 1
          ? "Deleted link"
          : `Deleted ${pendingDelete.length} links`,
        { variant: "success" }
      );
      setPendingDelete(null);
      setRowSelectionModel([]);
      setReloadKey((k) => k + 1);
    } catch {
      setPendingDelete(null);
    }
  };

  const selectedLinks = useMemo(
    () => links.filter((l) => rowSelectionModel.includes(l.id)),
    [links, rowSelectionModel]
  );
  const selectedCount = selectedLinks.length;

  const openActionsMenu = (e: React.MouseEvent<HTMLElement>) => {
    setActionsAnchorEl(e.currentTarget);
  };
  const closeActionsMenu = () => setActionsAnchorEl(null);

  const handleActionEdit = () => {
    closeActionsMenu();
    if (selectedLinks.length === 1) {
      openEdit(selectedLinks[0]);
    }
  };

  const handleActionDelete = () => {
    closeActionsMenu();
    if (selectedLinks.length > 0) {
      confirmDelete(selectedLinks);
    }
  };

  const handleActionDuplicate = async () => {
    closeActionsMenu();
    if (selectedLinks.length === 0) return;
    try {
      for (const row of selectedLinks) {
        await createUrl(source, sourceId, {
          name: row.name,
          url: row.url,
          sort_order: row.sort_order,
        });
      }
      enqueueSnackbar(
        selectedLinks.length === 1
          ? "Duplicated link"
          : `Duplicated ${selectedLinks.length} links`,
        { variant: "success" }
      );
      setRowSelectionModel([]);
      setReloadKey((k) => k + 1);
    } catch {
      enqueueSnackbar("Error duplicating links", { variant: "error" });
    }
  };

  const columns: GridColDef<Url>[] = useMemo(
    () => [
      { field: "name", headerName: "Name", flex: 1.5, minWidth: 180 },
      {
        field: "url",
        headerName: "URL",
        flex: 3,
        minWidth: 280,
        renderCell: (params) => {
          const v = params.value as string;
          if (!v) return null;
          return (
            <Link
              href={v}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              underline="hover"
              sx={{ overflow: "hidden", textOverflow: "ellipsis" }}
            >
              {v}
            </Link>
          );
        },
      },
    ],
    []
  );

  if (loading && links.length === 0) {
    return <Skeleton variant="rectangular" height={300} />;
  }

  return (
    <Box
      className="detail-view-card"
      sx={{
        width: "100%",
        boxShadow: "none",
      }}
    >
      {canEdit ? (
        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
          <CreateFab2 onClick={openAdd} size="small" title="New link" />
          <Button
            variant="contained"
            disabled={selectedCount === 0}
            onClick={openActionsMenu}
            endIcon={<KeyboardArrowDownIcon />}
            sx={actionsMenuButtonSx}
          >
            ACTIONS
          </Button>
          <Menu
            anchorEl={actionsAnchorEl}
            open={Boolean(actionsAnchorEl)}
            onClose={closeActionsMenu}
          >
            <MenuItem
              onClick={handleActionEdit}
              disabled={selectedCount !== 1}
            >
              Edit
            </MenuItem>
            <MenuItem onClick={handleActionDuplicate}>
              Duplicate{selectedCount > 1 ? ` (${selectedCount})` : ""}
            </MenuItem>
            <MenuItem onClick={handleActionDelete}>
              Delete{selectedCount > 1 ? ` (${selectedCount})` : ""}
            </MenuItem>
          </Menu>
        </Stack>
      ) : null}

      <DataGridPro<Url>
        rows={links}
        columns={columns}
        autoHeight
        checkboxSelection
        disableRowSelectionOnClick
        rowSelectionModel={rowSelectionModel}
        onRowSelectionModelChange={(m) => setRowSelectionModel(m)}
        onRowClick={(params) => canEdit && openEdit(params.row)}
        sx={{
          ...commonDataGridPremiumSx,
          ".MuiDataGrid-cell": { cursor: canEdit ? "pointer" : "default" },
        }}
      />

      <UrlDrawer
        open={drawerOpen}
        source={source}
        sourceId={sourceId}
        link={editingUrl}
        onClose={() => setDrawerOpen(false)}
        onSaved={handleSaved}
      />

      <AlertDialog
        title={
          pendingDelete && pendingDelete.length > 1
            ? `Delete ${pendingDelete.length} links?`
            : "Delete link?"
        }
        open={pendingDelete !== null}
        description="Permanently remove from this record. This cannot be undone."
        handleClose={() => setPendingDelete(null)}
      >
        <Button onClick={() => setPendingDelete(null)} color="primary">
          No
        </Button>
        <Button onClick={handleConfirmedDelete} color="primary" autoFocus>
          Yes
        </Button>
      </AlertDialog>
    </Box>
  );
}
